import uuidv4 from 'uuid/v4';
import download from 'downloadjs';
import {
  take,
  put,
  select,
  takeLatest,
  race,
  all,
  call,
} from 'redux-saga/effects';
import _ from 'lodash';
import {
  OPEN_DIALOG,
  OK_DIALOG,
  CLOSE_DIALOG,
  OTHER_DIALOG,
} from '../../../actions/uiActions';
import history from '../../../stores/browserHistory';
import { getToken } from '../../../selectors/authSelectors';
import { getDisclaimer } from '../selectors/ehubSelector';
import { myAxaFetchWithToken } from '../../../utils/myAxaFetchWithToken';
import { resolveLocalizedText } from '../../../utils';
import { COMPONENT_MOUNTED } from '../../../modules/e-hub/actions/ehubAction';
import {
  Disclaimer,
  DisclaimerTncToggle,
  DisclaimerTncCheckbox,
} from '../selectors/type';
import { goto } from '../../../actions/historyActions';
import { OPEN_DISCLAIMER, DOWNLOAD_DOCUMENT } from '../actions';
import { PostMsgParams } from '../../partners/types/partnerTypes';
import {
  isShowDisclaimer,
  getTncDisclaimerUrl,
  isNewTncDisclaimer,
  isShowTncDisclaimerToggle,
} from '../../../selectors/tncDisclaimersSelectors';
import { PUSH_HAVE_TNC_DISCLAIMER } from '../../../actions/tncDisclaimerActions';
import { trackPartnerServiceDisclaimer } from '../../../utils/generalGaEventAnalytics';

function* componentMounted(): any {
  return;
}

const getTncItemsFromDisclaimer = (
  items?: Array<DisclaimerTncCheckbox | DisclaimerTncToggle>,
) => {
  return items && items.length > 0
    ? items.map((item) => ({
        ...item,
        label: item.label ? resolveLocalizedText(item.label) : '',
        title: item.title ? resolveLocalizedText(item.title) : '',
      }))
    : [];
};

function* onDisclaimerAction(
  disclaimerId: string,
  params: PostMsgParams,
  isGoBack = false,
  okActionCallBack?: () => void,
): any {
  const disclaimer: Disclaimer = yield select(getDisclaimer, disclaimerId);
  if (!disclaimer) {
    return;
  }

  const isShowTncToggle = yield select(isShowTncDisclaimerToggle, disclaimerId);
  const tncToggles = isShowTncToggle
    ? getTncItemsFromDisclaimer(disclaimer.tncToggles)
    : [];
  const tncCheckboxes = getTncItemsFromDisclaimer(disclaimer.tncCheckboxes);

  const payload = {
    disclaimerId,
    modalType: 'lockedModal',
    title: disclaimer.title ? resolveLocalizedText(disclaimer.title) : '',
    body: disclaimer.message ? resolveLocalizedText(disclaimer.message) : '',
    okButtonText: disclaimer.okButton
      ? resolveLocalizedText(disclaimer.okButton.label)
      : '',
    otherButtonText: disclaimer.otherButton
      ? resolveLocalizedText(disclaimer.otherButton.label)
      : '',
    tncToggles,
    tncCheckboxes,
    otherDialog: {},
    okAction: () => {
      if (isGoBack) history.goBack();
      trackPartnerServiceDisclaimer(disclaimerId);
      okActionCallBack && okActionCallBack();
    },
    closeAction: () => {
      if (isGoBack) history.goBack();
    },
    otherAction: () => {
      if (isGoBack) history.goBack();
    },
  };
  yield put({
    type: OPEN_DIALOG,
    payload: payload,
  });
  const { OK, CLOSE, OTHER } = yield race({
    OK: take(OK_DIALOG),
    CLOSE: take(CLOSE_DIALOG),
    OTHER: take(OTHER_DIALOG),
  });

  const button = OK
    ? disclaimer.okButton
    : CLOSE
    ? disclaimer.closeButton
    : OTHER
    ? disclaimer.otherButton
    : undefined;
  const action = OK
    ? OK.payload
    : CLOSE
    ? CLOSE.payload
    : OTHER
    ? OTHER.payload
    : undefined;
  if (button) {
    const deepLink = button.deepLink;
    const query = deepLink ? deepLink.query : undefined;
    const nextDisclaimer = query ? query.disclaimer : undefined;
    const url = query ? query.url : undefined;
    const deepLinkId = query ? query.id : undefined;

    if (action) {
      action();
    }
    if (nextDisclaimer) {
      yield onDisclaimerAction(`${nextDisclaimer}`, params);
    } else if (deepLinkId) {
      const isNewDisclaimer = yield select(isNewTncDisclaimer, disclaimerId);
      if (isNewDisclaimer) {
        yield put({
          type: PUSH_HAVE_TNC_DISCLAIMER,
          payload: { serviceId: disclaimerId },
        });
      }
      yield call(goto, deepLinkId, undefined, params);
    } else if (url) {
      window.open(url, '_blank');
    }
  }
}

function* watchDisclaimerSaga(): Iterable<any> {
  while (true) {
    const action = yield take(OPEN_DISCLAIMER);
    const { disclaimerId } = action.payload;
    const isShowDisclaimerModel = yield select(isShowDisclaimer, disclaimerId);
    if (isShowDisclaimerModel) {
      yield onDisclaimerAction(
        action.payload.disclaimerId,
        action.payload.params,
        action.payload.isGoBack,
        action.payload.okActionCallBack,
      );
    } else {
      const disclaimerUrl = yield select(getTncDisclaimerUrl, disclaimerId);
      goto(disclaimerUrl);
    }
  }
}

function encodeUrlIfNeed(url: string) {
  const word = 'generic/documents/';
  const index = (url || '').indexOf(word);
  if (index > -1) {
    return (
      url.substring(0, index + word.length) +
      encodeURIComponent(url.substr(index + word.length))
    );
  }
  return url;
}

function* downloadPDF(action: any) {
  const token = select(getToken);
  if (token) {
    const res = yield call(myAxaFetchWithToken, encodeUrlIfNeed(action.url), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-axahk-msgid': uuidv4(),
      },
    });
    res.blob().then((blob: any) => {
      const fileName =
        res.headers.get('filename') || action.url.split('/').reverse()[0];
      const fileType = res.headers.get('filetype') || 'application/pdf';
      return download(blob, fileName, fileType);
    });
  }
}

function* disclaimerSaga() {
  yield takeLatest(COMPONENT_MOUNTED, componentMounted);
  yield takeLatest(DOWNLOAD_DOCUMENT, downloadPDF);
  yield all([watchDisclaimerSaga()]);
}

export default disclaimerSaga;
